<template>
  <header class="nav" :class="{ 'is-solid': solid, 'is-open': open }">
    <div class="nav-inner page">
      <router-link to="/home" class="nav-brand" @click.native="close">
        <BrandLogo/>
      </router-link>

      <nav class="nav-links">
        <router-link
          v-for="item in menus"
          :key="item.path"
          :to="item.path"
          class="nav-link"
          :class="{ active: isActive(item) }"
        >
          {{ item.name }}
          <span class="dot" :style="{ background: item.accent || 'var(--cyan)' }"></span>
        </router-link>
        <a
          class="nav-link nav-demo"
          :href="demo.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          在线演示
          <IconSvg name="external" class="demo-ico"/>
        </a>
      </nav>

      <div class="nav-actions">
        <a class="nav-phone" :href="`tel:${company.phone}`">
          <IconSvg name="phone"/>
          <span>{{ company.phone }}</span>
        </a>
        <router-link to="/contact" class="btn btn-primary nav-cta">免费咨询</router-link>
        <button class="nav-toggle" aria-label="菜单" @click="open = !open">
          <IconSvg :name="open ? 'close' : 'menu'"/>
        </button>
      </div>
    </div>

    <!-- 移动端抽屉 -->
    <transition name="drawer">
      <div v-if="open" class="nav-drawer">
        <router-link
          v-for="item in menus"
          :key="item.path"
          :to="item.path"
          class="drawer-link"
          @click.native="close"
        >
          <span>{{ item.name }}</span>
          <IconSvg name="arrow-right"/>
        </router-link>
        <a
          class="drawer-link drawer-demo"
          :href="demo.url"
          target="_blank"
          rel="noopener noreferrer"
          @click="close"
        >
          <span>在线演示</span>
          <IconSvg name="external"/>
        </a>
        <a class="drawer-phone" :href="`tel:${company.phone}`">
          <IconSvg name="phone"/> {{ company.phone }}
        </a>
      </div>
    </transition>
  </header>
</template>

<script>
import BrandLogo from '@/components/common/BrandLogo'
import IconSvg from '@/components/common/IconSvg'
import { businessLines, company, demo } from '@/data/site'

export default {
  name: 'SiteNav',
  components: { BrandLogo, IconSvg },
  data() {
    return {
      solid: false,
      open: false,
      company,
      demo,
      menus: [
        { name: '首页', path: '/home' },
        ...businessLines.map((l) => ({ name: l.navName || l.name, path: l.path, accent: l.accent, key: l.key })),
        { name: '解决方案', path: '/solutions' },
        { name: '行业洞察', path: '/insights' },
        { name: '关于我们', path: '/about' },
        { name: '联系我们', path: '/contact' }
      ]
    }
  },
  watch: {
    $route() {
      this.close()
      this.onScroll()
    },
    open(v) {
      document.body.style.overflow = v ? 'hidden' : ''
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    document.body.style.overflow = ''
  },
  methods: {
    onScroll() {
      this.solid = window.pageYOffset > 24
    },
    isActive(item) {
      if (item.path === '/home') return this.$route.path === '/home' || this.$route.path === '/'
      return this.$route.path === item.path
    },
    close() {
      this.open = false
      document.body.style.overflow = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--nav-h);
  z-index: 1000;
  transition: background 0.4s, border-color 0.4s, backdrop-filter 0.4s;
  border-bottom: 1px solid transparent;

  &.is-solid {
    background: rgba(4, 10, 22, 0.72);
    backdrop-filter: saturate(160%) blur(18px);
    border-bottom-color: var(--line);
  }
}

.nav-inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.nav-brand { flex-shrink: 0; }

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.nav-link {
  position: relative;
  /* 导航项不折行：任一业务线名称变长都会把整条导航挤成两行 */
  white-space: nowrap;
  padding: 9px 15px;
  font-size: 15px;
  color: var(--txt-2);
  border-radius: 10px;
  transition: color 0.3s, background 0.3s;

  .dot {
    position: absolute;
    left: 50%;
    bottom: 2px;
    width: 5px;
    height: 5px;
    margin-left: -2.5px;
    border-radius: 50%;
    opacity: 0;
    transform: scale(0.4);
    transition: all 0.35s;
    box-shadow: 0 0 10px currentColor;
  }

  &:hover { color: #fff; background: rgba(255, 255, 255, 0.05); }
  &.active { color: #fff; font-weight: 600; }
  &.active .dot { opacity: 1; transform: none; }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.nav-phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 0 2px;
  font-size: 14px;
  color: var(--txt-2);
  transition: color 0.3s;
  svg { width: 16px; height: 16px; color: var(--cyan); }
  &:hover { color: var(--cyan); }
}

.nav-cta { height: 44px; padding: 0 22px; font-size: 14px; }

.nav-demo {
  // 文本与外链图标必须保持同一行：该项被 flex 压缩后图标会换行掉到文字下方
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  color: var(--cyan);

  .demo-ico { width: 14px; height: 14px; opacity: 0.9; }
  &:hover { color: #fff; }
}

.drawer-demo { color: var(--cyan); }

.nav-toggle {
  display: none;
  width: 40px;
  height: 40px;
  padding: 9px;
  border-radius: 10px;
  border: 1px solid var(--line-strong);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  cursor: pointer;
}

.nav-drawer {
  position: absolute;
  top: var(--nav-h);
  left: 0;
  right: 0;
  padding: 14px 18px 24px;
  background: rgba(4, 10, 22, 0.97);
  backdrop-filter: blur(18px);
  border-bottom: 1px solid var(--line);
  display: flex;
  flex-direction: column;
}

.drawer-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 6px;
  font-size: 16px;
  color: var(--txt);
  border-bottom: 1px solid rgba(120, 176, 255, 0.08);
  svg { width: 18px; height: 18px; color: var(--txt-3); }
  &:active { color: var(--cyan); }
}

.drawer-phone {
  margin-top: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  border-radius: 999px;
  border: 1px solid var(--line-strong);
  color: var(--cyan);
  svg { width: 18px; height: 18px; }
}

.drawer-enter-active, .drawer-leave-active { transition: opacity 0.28s, transform 0.28s; }
.drawer-enter, .drawer-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 1180px) {
  .nav-links { gap: 0; }
  .nav-link { padding: 9px 10px; font-size: 14px; }
  .nav-phone { display: none; }
}

@media (max-width: 1040px) {
  .nav-links, .nav-cta { display: none; }
  .nav-toggle { display: block; }
}
</style>
